// Mock implementation of:
//   GET  /api/secure/me/mood/options
//   GET  /api/secure/me/mood/today
//   GET  /api/secure/me/mood/today-card
//   GET  /api/secure/me/mood/recent
//   POST /api/secure/me/mood/check-in
//
// Wire shape mirrors the server's `TodayResponse.MoodCheckInPrompt` and
// `MoodCheckInResponse` exactly so the cutover from mocks → real backend is a
// flag flip per resource. The picker config is intentionally identical to the
// constants in PatientMoodService.java — change both together.
//
// State is per-profile (by profile.id) and persisted to localStorage so the
// mock mood survives a page refresh, like the real DB would.

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.moodState.v1'
const TREND_DAYS = 7

const OPTIONS = [
  { code: 'GREAT', score: 5, label: 'Great', emoji: '😃', helperText: 'Energetic, present, on top of things.', tone: 'sage' },
  { code: 'GOOD',  score: 4, label: 'Good',  emoji: '🙂', helperText: 'Steady. A bit of light in the day.',     tone: 'ocean' },
  { code: 'OK',    score: 3, label: 'OK',    emoji: '😐', helperText: 'Neutral. Coasting through.',             tone: 'neutral' },
  { code: 'OFF',   score: 2, label: 'Off',   emoji: '😕', helperText: 'Foggy or unsettled — but functional.',   tone: 'amber' },
  { code: 'LOW',   score: 1, label: 'Low',   emoji: '😢', helperText: "Heavy. It's OK to take it easy today.",  tone: 'rose' },
]

const FACTORS = [
  { code: 'SLEEP',         label: 'Sleep',         emoji: '😴' },
  { code: 'ANXIETY',       label: 'Anxiety',       emoji: '😬' },
  { code: 'STRESS',        label: 'Stress',        emoji: '🔥' },
  { code: 'ENERGY',        label: 'Energy',        emoji: '⚡' },
  { code: 'WORK',          label: 'Work',          emoji: '💼' },
  { code: 'FAMILY',        label: 'Family',        emoji: '🏠' },
  { code: 'RELATIONSHIPS', label: 'Relationships', emoji: '🧡' },
  { code: 'BODY',          label: 'Body',          emoji: '💪' },
  { code: 'MEDICATION',    label: 'Medication',    emoji: '💊' },
  { code: 'THERAPY',       label: 'Therapy',       emoji: '🔄' },
]

const OPTION_BY_CODE = Object.fromEntries(OPTIONS.map((o) => [o.code, o]))
const FACTOR_CODES = new Set(FACTORS.map((f) => f.code))

// --- Storage helpers ---------------------------------------------------------

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}

function writeState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    /* ignore quota errors */
  }
}

function dayKey(d = new Date()) {
  // Local-day key. Mirrors the server's IST grouping closely enough for mock
  // purposes — a real backend uses Asia/Kolkata strictly.
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function profileEntry(profileId) {
  const all = readState()
  return all[profileId] || { byDate: {} }
}

function persistProfile(profileId, entry) {
  const all = readState()
  all[profileId] = entry
  writeState(all)
}

// --- Public mock surface -----------------------------------------------------

export async function getOptions() {
  await delay(80)
  return { options: OPTIONS, factors: FACTORS }
}

export async function getTodaysMood(profile) {
  await delay(80)
  if (!profile) return null
  const entry = profileEntry(profile.id)
  return entry.byDate[dayKey()] || null
}

export async function getRecent(profile) {
  await delay(80)
  if (!profile) return emptyTrend()
  const entry = profileEntry(profile.id)
  return buildTrend(entry.byDate)
}

export async function getTodayCard(profile) {
  await delay(80)
  return buildCard(profile)
}

export async function recordCheckIn(profile, payload) {
  await delay(120)
  if (!profile) return null
  if (!payload || !payload.moodCode) {
    throw new Error('moodCode is required')
  }
  const opt = OPTION_BY_CODE[payload.moodCode]
  if (!opt) throw new Error(`unknown moodCode: ${payload.moodCode}`)

  const factors = (payload.factors || [])
    .filter((c) => FACTOR_CODES.has(c))
    .filter((c, i, arr) => arr.indexOf(c) === i)
    .slice(0, 6)

  const note = (payload.note || '').trim().slice(0, 500) || null

  const saved = {
    id: `mood_${profile.id}_${Date.now()}`,
    moodCode: opt.code,
    score: opt.score,
    note,
    factors: factors.length ? factors : null,
    checkedInAt: new Date().toISOString(),
  }

  const entry = profileEntry(profile.id)
  entry.byDate = { ...entry.byDate, [dayKey()]: saved }
  persistProfile(profile.id, entry)
  return saved
}

// --- Helpers exposed for todayApi --------------------------------------------

export function buildCardSync(profile) {
  // Synchronous version used by todayApi's aggregate payload — keeps the
  // /me/today mock single-shot.
  if (!profile) return null
  return assembleCard(profile)
}

async function buildCard(profile) {
  if (!profile) return null
  return assembleCard(profile)
}

function assembleCard(profile) {
  const entry = profileEntry(profile.id)
  const today = entry.byDate[dayKey()] || null
  return {
    checkedInToday: !!today,
    prompt: today ? "How's the rest of your day going?" : 'How are you feeling today?',
    options: OPTIONS,
    factors: FACTORS,
    todaysMood: today,
    recent: buildTrend(entry.byDate),
  }
}

function buildTrend(byDate) {
  const out = []
  const today = new Date()
  for (let i = TREND_DAYS - 1; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const key = dayKey(d)
    const e = byDate[key]
    out.push({
      date: key,
      score: e ? e.score : null,
      moodCode: e ? e.moodCode : null,
    })
  }
  return out
}

function emptyTrend() {
  const out = []
  const today = new Date()
  for (let i = TREND_DAYS - 1; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    out.push({ date: dayKey(d), score: null, moodCode: null })
  }
  return out
}

// Test/demo helper — reset the mock mood log for a profile, or all profiles
// if no id is given.
export function __resetMoodMockState(profileId) {
  if (!profileId) {
    try { localStorage.removeItem(STORAGE_KEY) } catch (e) { /* ignore */ }
    return
  }
  const all = readState()
  delete all[profileId]
  writeState(all)
}

// Pre-seed a synthetic 7-day trend for demo profiles so the sparkline isn't
// empty on first render. Called by todayApi on first read.
export function ensureSeed(profileId, seed) {
  if (!profileId) return
  const all = readState()
  const entry = all[profileId]
  if (entry && entry.byDate && Object.keys(entry.byDate).length > 0) return
  const today = new Date()
  const byDate = {}
  for (let i = TREND_DAYS - 1; i >= 1; i--) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    const code = seed[TREND_DAYS - 1 - i] || null
    if (!code) continue
    const opt = OPTION_BY_CODE[code]
    if (!opt) continue
    byDate[dayKey(d)] = {
      id: `mood_seed_${profileId}_${i}`,
      moodCode: opt.code,
      score: opt.score,
      note: null,
      factors: null,
      checkedInAt: d.toISOString(),
    }
  }
  all[profileId] = { byDate }
  writeState(all)
}
